import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';

import { useGetInvestmentFundQuery } from 'api/main';
import { getSubscriptionData, parseNumber, parseSum } from 'utils/helpers';
import { texts } from 'utils/languages/fr';

import { StyledTableRow } from 'components';
import { TableCell } from '@mui/material';

const PortfolioRow = ({
    subscription,
    capitalAccounts,
    shareSubcategories,
    capitalCalls,
    closings,
    hideColumns,
    navs,
}) => {
    const { investingSum } = subscription.investmentCharacteristics;
    const { data: fund } = useGetInvestmentFundQuery(subscription?.investmentFund?.id, {
        skip: !subscription?.investmentFund?.id,
    });

    const {
        investmentSumCalled,
        sharesNumber,
        currentShareValue,
        valuation, // for open ended funds, valuation is based on the latest NAV, for closed ended funds it's based on the latest closing
        latestLiquidityDate, // latest capital account issue date
        subcategory,
    } = getSubscriptionData({
        subscription,
        shareSubcategories,
        capitalAccounts,
        capitalCalls,
        closings,
        fund,
        navs,
    });
    const dpi = investmentSumCalled === 0 ? 0 : 0 / investmentSumCalled; // distributions to be added later
    const rvpi = investmentSumCalled === 0 ? 0 : valuation / investmentSumCalled;
    const tvpi = dpi + rvpi;
    const irr =
        Number.isNaN(subscription.internalRateOfReturn?.rate) ||
        !subscription.internalRateOfReturn?.rate
            ? 0
            : parseNumber(subscription.internalRateOfReturn.rate * 100, 1);
    const liquidityDate = useMemo(() => {
        if (fund?.evergreen) {
            return latestLiquidityDate ? moment(latestLiquidityDate).format('L') : '-';
        } else {
            return latestLiquidityDate ? moment().format('L') : '-';
        }
    }, [fund, latestLiquidityDate]);

    return (
        <StyledTableRow>
            {/* FUND */}
            <TableCell>{subscription.investmentFund.name}</TableCell>
            {/* INVESTMENT_DATE */}
            <TableCell>{moment(subscription.finishedSubscriptionAt).format('L')}</TableCell>
            {/* CATEGORIE_DE_PARTS */}
            <TableCell>{subcategory?.name}</TableCell>
            {/* SHARES_NUMBER */}
            <TableCell>
                {sharesNumber ? parseNumber(sharesNumber, fund?.numberOfDecimals || 3) : '-'}
            </TableCell>
            {/* MONTANT_INVESTI */}
            <TableCell>{parseSum(investingSum)}</TableCell>
            {/* MONTANT_APPELE */}
            <TableCell>{parseSum(investmentSumCalled)}</TableCell>
            {/* SUBSCRIBED_NET_ASSET_VALUE */}
            <TableCell>{parseSum(subscription?.investmentCharacteristics?.shareValue)}</TableCell>
            {/* LATEST_NAV_DATE */}
            <TableCell>{liquidityDate}</TableCell>
            {/* CURRENT_NET_ASSET_VALUE */}
            <TableCell>{parseSum(currentShareValue)}</TableCell>
            {/* VALUATION */}
            <TableCell>{valuation ? parseSum(valuation) : '-'}</TableCell>
            {/* IRR */}
            <TableCell>{irr > 0 ? irr + '%' : '-'}</TableCell>
            {/* TVPI */}
            <TableCell>{tvpi ? tvpi.toFixed(2) : '-'}</TableCell>
            {/* PARTNER */}
            {!hideColumns && (
                <TableCell>
                    {subscription.distributorCompany?.corporateName || texts.PEQAN}
                </TableCell>
            )}
        </StyledTableRow>
    );
};

PortfolioRow.propTypes = {
    subscription: PropTypes.object,
    capitalAccounts: PropTypes.array,
    shareSubcategories: PropTypes.array,
    capitalCalls: PropTypes.array,
    closings: PropTypes.array,
    navs: PropTypes.array,
    hideColumns: PropTypes.bool,
};

export default PortfolioRow;
